import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageHero, SectionHeader, StatsSection } from '@/components/common/Sections';
import { CTABanner } from '@/components/common/Cards';
import { STATISTICS, IMAGES, MILESTONES } from '@/lib/constants';
import { Target, Eye, Heart, Users, Globe, Trophy } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/common/SEO';

export default function AboutPage() {
  const coreValues = [
    { icon: Heart, title: "Customer First", description: "Every decision we make starts with our customers' needs and aspirations." },
    { icon: Trophy, title: "Excellence", description: "We strive for excellence in every service we deliver, every day." },
    { icon: Users, title: "Teamwork", description: "150+ professionals working together with a collaborative spirit." },
    { icon: Globe, title: "Global Mindset", description: "Thinking globally while acting locally across India & UK." },
  ];

  return (
    <div className="page-transition">
      <SEO title="About Us" description="Osaka Group's story — three decades of transforming travel dreams into unforgettable journeys. 16 key milestones from 1993 to 2023. Vision, mission & core values." path="/about" />
      <PageHero
        subtitle="Three decades of transforming travel dreams into unforgettable journeys"
        backgroundImage={IMAGES.airport}
      />

      {/* Brand Story */}
      <section data-testid="brand-story" className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              overline="About Osaka Group"
              title="A Legacy of Trust & Excellence"
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-none text-[#4A5568]"
            >
              <p className="text-lg leading-relaxed mb-6">
                Founded in 1993 by Dr. P.B. Boss, Osaka Group began as a small travel agency in Kerala with a
                simple vision: to make quality travel services accessible to everyone. What started as a
                one-man operation has now grown into one of India's most respected travel management companies.
              </p>
              <p className="text-lg leading-relaxed mb-6">
                Over three decades, we have served over a million happy travelers, built partnerships with
                leading airlines and hospitality brands, and expanded our presence to 8 locations across
                India and the United Kingdom.
              </p>
              <p className="text-lg leading-relaxed">
                Today, Osaka Group comprises five specialized companies, each dedicated to a specific segment
                of travel services. From visa processing to corporate travel management, from study abroad
                consultancy to our revolutionary Osaka Connect super app — we are truly a one-stop destination
                for all travel needs.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section data-testid="vision-mission" className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 border-t-4 border-[#D4AF37]"
            >
              <div className="w-14 h-14 bg-[#0A1F3D] flex items-center justify-center mb-6">
                <Eye size={28} className="text-[#D4AF37]" />
              </div>
              <h3 className="text-2xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-4">
                Our Vision
              </h3>
              <p className="text-[#4A5568] leading-relaxed">
                To be the most trusted and innovative travel management company in Asia, setting new standards
                in service excellence and customer satisfaction while making world-class travel accessible to all.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 border-t-4 border-[#20A39E]"
            >
              <div className="w-14 h-14 bg-[#0A1F3D] flex items-center justify-center mb-6">
                <Target size={28} className="text-[#20A39E]" />
              </div>
              <h3 className="text-2xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-4">
                Our Mission
              </h3>
              <p className="text-[#4A5568] leading-relaxed">
                To deliver exceptional travel experiences through personalized service, innovative technology,
                and a passionate team that treats every journey as a unique story waiting to be written.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Milestones Timeline */}
      <section data-testid="journey-timeline" className="section-padding bg-white overflow-hidden">
        <div className="container-custom">
          <SectionHeader
            overline="Our Journey"
            title="Key Milestones"
            description="Converting travel dreams of our tourists into reality — from a single desk in 1993 to a pan-India travel powerhouse."
          />

          <div className="relative max-w-5xl mx-auto">
            {/* Timeline center line - desktop */}
            <div className="hidden md:block absolute left-1/2 -translate-x-1/2 w-[2px] h-full bg-gradient-to-b from-[#D4AF37] via-[#D4AF37]/40 to-[#0A1F3D]" />
            {/* Timeline line - mobile */}
            <div className="md:hidden absolute left-6 w-[2px] h-full bg-gradient-to-b from-[#D4AF37] via-[#D4AF37]/40 to-[#0A1F3D]" />

            {MILESTONES.map((item, index) => {
              const isLeft = index % 2 === 0;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: 0.05, duration: 0.5 }}
                  data-testid={`milestone-${index}`}
                  className="relative mb-10 md:mb-14"
                >
                  {/* Desktop layout */}
                  <div className="hidden md:grid md:grid-cols-[1fr_48px_1fr] items-center gap-0">
                    {/* Left content */}
                    <div className={`${isLeft ? 'pr-8' : ''}`}>
                      {isLeft && (
                        <div className="text-right">
                          <span className="inline-block px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-sm font-bold tracking-wider mb-2">
                            {item.year}
                          </span>
                          <h4 className="text-xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-1">
                            {item.title}
                          </h4>
                          <p className="text-[#4A5568] text-sm leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Center dot */}
                    <div className="flex justify-center">
                      <div className="w-4 h-4 bg-[#D4AF37] rounded-full border-4 border-white shadow-md ring-2 ring-[#D4AF37]/20 z-10" />
                    </div>

                    {/* Right content */}
                    <div className={`${!isLeft ? 'pl-8' : ''}`}>
                      {!isLeft && (
                        <div className="text-left">
                          <span className="inline-block px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-sm font-bold tracking-wider mb-2">
                            {item.year}
                          </span>
                          <h4 className="text-xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-1">
                            {item.title}
                          </h4>
                          <p className="text-[#4A5568] text-sm leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mobile layout */}
                  <div className="md:hidden flex items-start gap-5">
                    <div className="flex-shrink-0 w-3 h-3 mt-2 bg-[#D4AF37] rounded-full border-2 border-white shadow ring-2 ring-[#D4AF37]/20 z-10 ml-[18px]" />
                    <div className="flex-1">
                      <span className="inline-block px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-bold tracking-wider mb-1">
                        {item.year}
                      </span>
                      <h4 className="text-lg font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-1">
                        {item.title}
                      </h4>
                      <p className="text-[#4A5568] text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* End marker */}
            <div className="flex md:justify-center justify-start md:ml-0 ml-[18px]">
              <div className="w-6 h-6 bg-[#0A1F3D] rounded-full border-4 border-white shadow-md ring-2 ring-[#0A1F3D]/20 z-10 flex items-center justify-center">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full" />
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/leadership">
              <Button
                data-testid="meet-leadership-btn"
                variant="outline"
                className="border-[#0A1F3D] text-[#0A1F3D] hover:bg-[#0A1F3D] hover:text-white px-8 py-5 rounded-none"
              >
                Meet Our Leadership
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section data-testid="core-values" className="section-padding bg-[#0A1F3D] relative grain-overlay">
        <div className="container-custom relative z-10">
          <SectionHeader
            overline="What We Stand For"
            title="Our Core Values"
            description="The principles that guide everything we do at Osaka Group."
            light
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-white/10 flex items-center justify-center">
                  <value.icon size={32} className="text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold font-['Playfair_Display'] text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <StatsSection stats={STATISTICS} />

      {/* CTA */}
      <CTABanner />
    </div>
  );
}
